import Cookies from "js-cookie";
import type { UserTokens } from "./auth-user.ts";

// CognitoIdentityServiceProvider.7dvn6q3kpa5gdgb808denp4fsq.dadfe618-4036-4176-a9ba-bbf9def812e9.accessToken
// CognitoIdentityServiceProvider.7dvn6q3kpa5gdgb808denp4fsq.dadfe618-4036-4176-a9ba-bbf9def812e9.clockDrift		0
// CognitoIdentityServiceProvider.7dvn6q3kpa5gdgb808denp4fsq.dadfe618-4036-4176-a9ba-bbf9def812e9.idToken
// CognitoIdentityServiceProvider.7dvn6q3kpa5gdgb808denp4fsq.dadfe618-4036-4176-a9ba-bbf9def812e9.refreshToken
// CognitoIdentityServiceProvider.7dvn6q3kpa5gdgb808denp4fsq.LastAuthUser

type AuthCookieStorageConfig = {
	readonly appClientId: string;
	readonly cookieDomain: string;
	readonly cookiesSecure: boolean;
};

function createAuthCookieStorage({
	appClientId,
	cookieDomain,
	cookiesSecure,
}: AuthCookieStorageConfig) {
	const cookieAttributes = {
		secure: cookiesSecure,
		path: "/",
		domain: cookieDomain,
		// Days
		expires: 93,
	};
	const cookieNamePrefix = `bm.${appClientId}.`;
	const accessCookieName = `${cookieNamePrefix}access`;
	const idCookieName = `${cookieNamePrefix}id`;
	const refreshCookieName = `${cookieNamePrefix}refresh`;

	// Legacy cookies
	const legacyCookiesPrefix = `CognitoIdentityServiceProvider.${appClientId}.`;

	return {
		get(): UserTokens | undefined {
			// Check for legacy cookies and migrate to modern
			const lastAuthUserCookieName = `${legacyCookiesPrefix}LastAuthUser`;
			const authUserId = Cookies.get(lastAuthUserCookieName) ?? undefined;
			if (authUserId !== undefined) {
				const authUserCookiesPrefix = `${legacyCookiesPrefix}${authUserId}.`;
				const legacyAccessTokenName = `${authUserCookiesPrefix}accessToken`;
				const legacyIdTokenName = `${authUserCookiesPrefix}idToken`;
				const legacyRefreshTokenName = `${authUserCookiesPrefix}refreshToken`;
				const legacyClockDriftName = `${authUserCookiesPrefix}clockDrift`;

				const legacyAccessTokenValue =
					Cookies.get(legacyAccessTokenName) ?? undefined;
				const legacyIdTokenValue = Cookies.get(legacyIdTokenName) ?? undefined;
				const legacyRefreshTokenValue =
					Cookies.get(legacyRefreshTokenName) ?? undefined;
				if (
					legacyAccessTokenValue !== undefined &&
					legacyIdTokenValue !== undefined &&
					legacyRefreshTokenValue !== undefined
				) {
					Cookies.set(
						accessCookieName,
						legacyAccessTokenValue,
						cookieAttributes,
					);
					Cookies.set(idCookieName, legacyIdTokenValue, cookieAttributes);
					Cookies.set(
						refreshCookieName,
						legacyRefreshTokenValue,
						cookieAttributes,
					);

					Cookies.remove(legacyAccessTokenName);
					Cookies.remove(legacyIdTokenName);
					Cookies.remove(legacyRefreshTokenName);
					Cookies.remove(legacyClockDriftName);
					Cookies.remove(lastAuthUserCookieName);
				}
			}

			// Local/modern
			const accessValue = Cookies.get(accessCookieName);
			const idValue = Cookies.get(idCookieName);
			const refreshValue = Cookies.get(refreshCookieName);
			if (
				accessValue === undefined ||
				idValue === undefined ||
				refreshValue === undefined
			) {
				return undefined;
			}

			return {
				accessToken: { accessToken: accessValue },
				idToken: { idToken: idValue },
				refreshToken: { refreshToken: refreshValue },
			};
		},
		set({
			accessToken: { accessToken },
			idToken: { idToken },
			refreshToken: { refreshToken },
		}: UserTokens) {
			Cookies.set(accessCookieName, accessToken, cookieAttributes);
			Cookies.set(idCookieName, idToken, cookieAttributes);
			Cookies.set(refreshCookieName, refreshToken, cookieAttributes);
		},
		clear() {
			Cookies.remove(accessCookieName, cookieAttributes);
			Cookies.remove(idCookieName, cookieAttributes);
			Cookies.remove(refreshCookieName, cookieAttributes);
		},
	};
}

type AuthCookieStorage = ReturnType<typeof createAuthCookieStorage>;

export type { AuthCookieStorage };
export { createAuthCookieStorage };
